import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
    Send,
    Image,
    Calendar,
    Twitter,
    Facebook,
    Instagram,
    Linkedin,
    Clock,
    Check,
    X,
    Trash2,
    MoreVertical,
    Music2
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TikTok icon component
const TikTokIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
    </svg>
);

const PLATFORMS = [
    { id: 'twitter', name: 'X (Twitter)', icon: Twitter, color: 'text-sky-500' },
    { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'text-blue-600' },
    { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'text-pink-500' },
    { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: 'text-blue-700' },
    { id: 'tiktok', name: 'TikTok', icon: TikTokIcon, color: 'text-pink-600' },
];

export const SocialComposer = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [content, setContent] = useState('');
    const [selectedPlatforms, setSelectedPlatforms] = useState(['twitter', 'facebook']);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        fetchPosts();
    }, []);

    const fetchPosts = async () => {
        try {
            const res = await axios.get(`${API}/social-posts`);
            setPosts(res.data);
        } catch (error) {
            console.error('Error fetching posts:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePost = async () => {
        if (!content.trim()) {
            toast.error('Please enter some content');
            return;
        }
        if (selectedPlatforms.length === 0) {
            toast.error('Please select at least one platform');
            return;
        }

        setIsSubmitting(true);
        try {
            await axios.post(`${API}/social-posts`, {
                content: content.trim(),
                platforms: selectedPlatforms
            });
            toast.success('Post created successfully');
            setContent('');
            fetchPosts();
        } catch (error) {
            console.error('Error creating post:', error);
            toast.error('Failed to create post');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePublish = async (postId) => {
        try {
            await axios.post(`${API}/social-posts/${postId}/publish`);
            toast.success('Post published!');
            fetchPosts();
        } catch (error) {
            console.error('Error publishing post:', error);
            toast.error('Failed to publish post');
        }
    };

    const handleDelete = async (postId) => {
        try {
            await axios.delete(`${API}/social-posts/${postId}`);
            toast.success('Post deleted');
            fetchPosts();
        } catch (error) {
            console.error('Error deleting post:', error);
            toast.error('Failed to delete post');
        }
    };

    const togglePlatform = (platformId) => {
        setSelectedPlatforms(prev =>
            prev.includes(platformId)
                ? prev.filter(id => id !== platformId)
                : [...prev, platformId]
        );
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'published':
                return <span className="flex items-center gap-1 text-xs text-green-600"><Check className="w-3 h-3" />Published</span>;
            case 'scheduled':
                return <span className="flex items-center gap-1 text-xs text-blue-600"><Clock className="w-3 h-3" />Scheduled</span>;
            case 'failed':
                return <span className="flex items-center gap-1 text-xs text-red-600"><X className="w-3 h-3" />Failed</span>;
            default:
                return <span className="flex items-center gap-1 text-xs text-muted-foreground">Draft</span>;
        }
    };

    const charCount = content.length;
    const maxChars = 280;

    return (
        <div className="space-y-6" data-testid="social-composer-page">
            {/* Header */}
            <div>
                <h1 className="text-2xl lg:text-3xl font-bold">Social Composer</h1>
                <p className="text-muted-foreground mt-1">
                    Create and schedule posts for your social platforms
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Composer */}
                <div className="lg:col-span-2">
                    <Card>
                        <CardHeader>
                            <CardTitle>Create Post</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Text Input */}
                            <div>
                                <Textarea
                                    placeholder="What's on your mind? Share an update with your audience..."
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    className="min-h-[150px] resize-none"
                                    data-testid="post-content"
                                />
                                <div className="flex justify-end mt-2">
                                    <span className={`text-xs ${charCount > maxChars ? 'text-red-500' : 'text-muted-foreground'}`}>
                                        {charCount}/{maxChars}
                                    </span>
                                </div>
                            </div>

                            {/* Platform Selection */}
                            <div>
                                <label className="text-sm font-medium mb-3 block">Post to</label>
                                <div className="flex flex-wrap gap-3">
                                    {PLATFORMS.map((platform) => (
                                        <div
                                            key={platform.id}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                                                selectedPlatforms.includes(platform.id)
                                                    ? 'border-primary bg-primary/5'
                                                    : 'border-border hover:border-primary/50'
                                            }`}
                                            onClick={() => togglePlatform(platform.id)}
                                            data-testid={`platform-${platform.id}`}
                                        >
                                            <Checkbox
                                                checked={selectedPlatforms.includes(platform.id)}
                                                onCheckedChange={() => togglePlatform(platform.id)}
                                            />
                                            <platform.icon className={`w-4 h-4 ${platform.color}`} />
                                            <span className="text-sm">{platform.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center justify-between pt-4 border-t">
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" className="gap-2">
                                        <Image className="w-4 h-4" />
                                        Add Media
                                    </Button>
                                    <Button variant="outline" size="sm" className="gap-2">
                                        <Calendar className="w-4 h-4" />
                                        Schedule
                                    </Button>
                                </div>
                                <Button
                                    onClick={handlePost}
                                    disabled={isSubmitting || !content.trim() || selectedPlatforms.length === 0}
                                    className="gap-2"
                                    data-testid="post-btn"
                                >
                                    <Send className="w-4 h-4" />
                                    Post Now
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Recent Posts */}
                <div>
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Recent Posts</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="space-y-3">
                                    {[1, 2, 3].map((i) => (
                                        <div key={i} className="h-20 bg-muted rounded animate-pulse" />
                                    ))}
                                </div>
                            ) : posts.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Send className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">No posts yet</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {posts.slice(0, 5).map((post) => (
                                        <div
                                            key={post.id}
                                            className="p-3 rounded-lg border bg-card hover:shadow-sm transition-shadow"
                                            data-testid={`post-${post.id}`}
                                        >
                                            <div className="flex items-start justify-between gap-2 mb-2">
                                                <p className="text-sm line-clamp-2">{post.content}</p>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0">
                                                            <MoreVertical className="w-3 h-3" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        {post.status !== 'published' && (
                                                            <DropdownMenuItem onClick={() => handlePublish(post.id)}>
                                                                <Send className="w-4 h-4 mr-2" />
                                                                Publish Now
                                                            </DropdownMenuItem>
                                                        )}
                                                        <DropdownMenuItem
                                                            className="text-destructive"
                                                            onClick={() => handleDelete(post.id)}
                                                        >
                                                            <Trash2 className="w-4 h-4 mr-2" />
                                                            Delete
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-1">
                                                    {post.platforms?.map((p) => {
                                                        const platform = PLATFORMS.find(pl => pl.id === p);
                                                        if (!platform) return null;
                                                        return (
                                                            <platform.icon
                                                                key={p}
                                                                className={`w-3 h-3 ${platform.color}`}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                                {getStatusBadge(post.status)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default SocialComposer;
